<script setup>
import {computed, inject, reactive, ref, toRaw} from "vue";
//import functions
import {required} from "@vuelidate/validators";
import {showMessage} from "@/utils/showMessage.js";
//import components
import {InputText, FloatLabel, Dialog, Listbox, Checkbox} from "primevue";
import {Icon} from "@iconify/vue";
//import stores
import {useUserStore} from "@/stores/user.js";
import {useHotOrdersStore} from "@/stores/hotOrders.js";
import {useMainOrdersStore} from "@/stores/mainOrders.js";
//import composables
import {useBreakpoint} from "@/composables/useBreakpoint.js";
import {useLoadingFlags} from "@/composables/useLoadingFlags.js";
import {useToast} from "primevue/usetoast";
import {useMarketMapStore} from "@/stores/marketMap.js";

//stores
const marketMapStore = useMarketMapStore();
const userStore = useUserStore();
const ordersStore = useMainOrdersStore();

//loading flags
const {
  saving,
  setSaving,
} = useLoadingFlags()

//composables
const {isXL} = useBreakpoint();
const toast = useToast();

//emits
const emit = defineEmits(['refresh']);

//props
const props = defineProps({
  selectedItem: {
    type: Object,
    required: true
  }
});

//injects
const visible = inject('isShowSettingsModal')

//component variables
const selectedTaker = ref(null)
const settings = reactive({
  "notifyChanges": props.selectedItem.notifyChanges,
  "isActive": props.selectedItem.isActive
})

//global functions
//component functions
const onSave = async () => {
  try {
    saving.value = true
    const res = await marketMapStore.action_updateMarket4({
      "game": props.selectedItem.game,
      "server": props.selectedItem.server,
      "link": props.selectedItem.link,
      "notifyChanges": settings.notifyChanges,
      "isActive": settings.isActive
    })
    if (res.result === true) {
      showMessage('Settings Changed.', 'success');
      emit('refresh')
      setTimeout(() => {
        visible.value = false
      }, 1000)
    } else {
      showMessage(res, 'error');
    }
  } catch (err) {
    showMessage(err, 'error');
  } finally {
    saving.value = false
  }
}

//hooks

//provides
</script>
<template>
  <Dialog v-model:visible="visible"
          :class="['max-w-3xl !border-0 !rounded-md !bg-[#0e1726]', isXL ? '' : 'w-full']"
          :position="isXL ? 'center' : 'bottom'"
          closable dismissable-mask draggable modal
          content-class="flex flex-col gap-4 overflow-hidden h-full w-full max-w-[calc(100vw-20px)] max-h-[calc(100vh-100px)]">
    <template #header>
      <div class="text-base py-3">
        Settings
      </div>
    </template>
    <template #closebutton="{closeCallback}">
      <div class="h-full w-[1px] bg-slate-700"></div>
      <button
          type="button"
          class="text-white-dark outline-none hover:text-red-500"
          @click="closeCallback"
      >
        <Icon icon="pajamas:close-xs"/>
      </button>
    </template>
    <div class="flex-1 flex flex-col gap-4 overflow-y-auto pt-4 pb-1 text-xs 2xl:text-sm min-w-80 max-w-full">
      <div class="grid grid-cols-12 gap-4">
        <div class="col-span-6 flex flex-col gap-0.5 text-xs 2xl:text-sm">
          <FloatLabel variant="on">
            <InputText id="game" :value="selectedItem.game" autocomplete="off" readonly
                       disabled
                       class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm p-filled !border-slate-800"/>
            <label for="game" class="!bg-slate-900 !text-slate-500">Game</label>
          </FloatLabel>
        </div>
        <div class="col-span-6 flex flex-col gap-0.5 text-xs 2xl:text-sm">
          <FloatLabel variant="on">
            <InputText id="server" :value="selectedItem.server" autocomplete="off" readonly
                       disabled
                       class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm p-filled !border-slate-800"/>
            <label for="server" class="!bg-slate-900 !text-slate-500">Server</label>
          </FloatLabel>
        </div>
        <div class="col-span-12 flex flex-col gap-0.5 text-xs 2xl:text-sm">
          <FloatLabel variant="on">
            <InputText id="link" :value="selectedItem.link" autocomplete="off" readonly
                       disabled
                       class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm p-filled !border-slate-800"/>
            <label for="link" class="!bg-slate-900 !text-slate-500">Link</label>
          </FloatLabel>
        </div>
      </div>
      <div class="grid grid-cols-2">
        <div class="flex items-center gap-2 cursor-pointer w-max">
          <Checkbox v-model="settings.isActive" inputId="isActive" name="isActive" :value="true" binary/>
          <label for="isActive" class="cursor-pointer select-none"> Active </label>
        </div>
        <div class="flex items-center gap-2 cursor-pointer w-max">
          <Checkbox v-model="settings.notifyChanges" inputId="notifyChanges" name="notifyChanges" :value="true" binary/>
          <label for="notifyChanges" class="cursor-pointer select-none"> Notify Changes </label>
        </div>
      </div>
    </div>

    <div class="flex justify-end gap-3 select-none">
      <button @click="onSave"
              class="btn btn-outline-dark flex items-center justify-center gap-3 disabled:opacity-50 py-1">
        <Icon v-if="saving" icon="eos-icons:bubble-loading" height="24"/>
        Save
      </button>
    </div>
  </Dialog>
</template>
<style></style>